import React from "react";
import { Users, FileText, HardHat, BadgeCheck, ArrowRight } from "lucide-react";
import { siteConfig } from "../siteConfig";
import Reveal from "./Reveal";

const steps = [
  { Icon: Users,      title: "Free Consultation",    body: "Fill out our form or give us a call. We'll learn about your project and answer your questions." },
  { Icon: FileText,   title: "Estimation & Proposal", body: "We visit your property, discuss your vision, and provide a detailed, no-obligation estimate." },
  { Icon: HardHat,    title: "Expert Cleaning",       body: "Our trained crew gets to work with the right pressure and technique for every surface, start to finish." },
  { Icon: BadgeCheck, title: "Final Walkthrough",     body: "We walk the property with you, make sure every surface is spotless, and answer any last questions." },
];

const chevronStyle = (first) => ({
  clipPath: first
    ? "polygon(0 0, calc(100% - 18px) 0, 100% 50%, calc(100% - 18px) 100%, 0 100%)"
    : "polygon(0 0, calc(100% - 18px) 0, 100% 50%, calc(100% - 18px) 100%, 0 100%, 18px 50%)",
});

const ProcessSection = () => (
  <section className="py-16 md:py-28 overflow-hidden" style={{ backgroundColor: siteConfig.colors.dark }}>
    <div className="max-w-7xl mx-auto px-5 md:px-8">

      {/* Label */}
      <div className="flex items-center gap-3 mb-8 lg:hidden">
        <span className="text-xs font-black uppercase tracking-widest" style={{ color: siteConfig.colors.primary }}>
          Our Process
        </span>
        <div className="flex-1 h-px" style={{ backgroundColor: siteConfig.colors.primary }} />
      </div>

      {/* ── Desktop: left headline + right 4-col steps ── */}
      <div className="hidden lg:grid lg:grid-cols-[300px_1fr] gap-12 items-start mb-16">

        {/* Left */}
        <Reveal as="div">
          <div className="flex items-center gap-3 mb-5">
            <span className="text-xs font-black uppercase tracking-widest" style={{ color: siteConfig.colors.primary }}>
              Our Process
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: siteConfig.colors.primary }} />
          </div>
          <h2 className="font-display font-black text-white leading-[1.05]" style={{ fontSize: "clamp(2rem,3.5vw,3rem)" }}>
            Simple Process.{" "}
            <span style={{ color: siteConfig.colors.primary }}>Exceptional Results.</span>
          </h2>
          <p className="mt-4 text-white/50 text-sm leading-relaxed max-w-xs">
            From the first call to the final walkthrough, we keep it clear, honest, and on schedule.
          </p>
        </Reveal>

        {/* Right — 4 columns */}
        <div className="grid grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 120} className="flex flex-col">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black shrink-0"
                  style={{ backgroundColor: siteConfig.colors.primary, color: "#fff" }}
                >
                  {i + 1}
                </div>
                {i < steps.length - 1 && (
                  <div className="flex flex-1 items-center gap-1">
                    <div className="flex-1 h-px border-t border-dashed border-white/20" />
                    <ArrowRight className="w-3.5 h-3.5 text-white/25 shrink-0" />
                  </div>
                )}
              </div>
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: `${siteConfig.colors.primary}18` }}
              >
                <s.Icon className="w-6 h-6" style={{ color: siteConfig.colors.primary }} />
              </div>
              <h3 className="font-display font-black text-white text-base mb-2">{s.title}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{s.body}</p>
            </Reveal>
          ))}
        </div>
      </div>

      {/* ── Mobile: headline + vertical step list ── */}
      <Reveal as="div" className="lg:hidden mb-10">
        <h2 className="font-display font-black text-white leading-[1.05] mb-2" style={{ fontSize: "clamp(1.8rem,6vw,2.5rem)" }}>
          Simple Process.{" "}
          <span style={{ color: siteConfig.colors.primary }}>Exceptional Results.</span>
        </h2>
        <p className="text-white/50 text-sm leading-relaxed mb-8">
          From the first call to the final walkthrough, we keep it clear, honest, and on schedule.
        </p>

        <div className="flex flex-col gap-0">
          {steps.map((s, i) => (
            <Reveal key={s.title} delay={i * 100} className="flex gap-4">
              {/* Left — number + vertical line */}
              <div className="flex flex-col items-center">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black shrink-0"
                  style={{ backgroundColor: siteConfig.colors.primary, color: "#fff" }}
                >
                  {i + 1}
                </div>
                {i < steps.length - 1 && (
                  <div className="flex-1 w-px mt-2 mb-2" style={{ backgroundColor: `${siteConfig.colors.primary}30` }} />
                )}
              </div>

              {/* Right — icon + text */}
              <div className={`flex-1 ${i < steps.length - 1 ? "pb-7" : ""}`}>
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                  style={{ backgroundColor: `${siteConfig.colors.primary}18` }}
                >
                  <s.Icon className="w-5 h-5" style={{ color: siteConfig.colors.primary }} />
                </div>
                <h3 className="font-display font-black text-white text-base mb-1">{s.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Reveal>

      {/* ── Chevron bar — desktop only ── */}
      <Reveal as="div" animation="fade" className="hidden lg:flex items-stretch gap-0.5">
        {steps.map((s, i) => (
          <div
            key={s.title}
            className="flex-1 flex items-center justify-center text-center px-5 py-5 text-sm font-black text-white uppercase tracking-wide"
            style={{
              backgroundColor: i % 2 === 0 ? `${siteConfig.colors.primary}18` : `${siteConfig.colors.primary}28`,
              ...chevronStyle(i === 0),
              letterSpacing: "0.05em",
            }}
          >
            {s.title}
          </div>
        ))}
      </Reveal>

    </div>
  </section>
);

export default ProcessSection;
